import React from "react";
import DashboardCardSkeleton from "../components/DashboardCardSkeleton";
import { IPayment, IOwnerKenloEndpoints } from "../types/ownerDashboard.types";
import { KenloPropertyOption } from "../utils/getKenloPropertyOptions";
import { usePatrimonySection } from "../hooks/usePatrimonySection";
import { isOwnerRequestPending } from "../utils/isOwnerRequestPending";
import PropertyReturns from "../PropertyReturns";
import FinancingInsights from "../FinancingInsights";

interface PatrimonySectionProps {
  accessToken: string | null;
  enabled: boolean;
  endpoints?: IOwnerKenloEndpoints;
  payments: IPayment[];
  propertyFilterOptions: KenloPropertyOption[];
  smDown: boolean;
  lgDown?: boolean;
}

export default function PatrimonySection({
  accessToken,
  enabled,
  endpoints,
  payments,
  propertyFilterOptions,
  smDown,
  lgDown = false,
}: PatrimonySectionProps) {
  const patrimony = usePatrimonySection({
    accessToken,
    enabled,
    endpoints,
  });

  if (!enabled) {
    return (
      <div className="grid w-full min-w-0 grid-cols-1 gap-4 xl:grid-cols-2">
        <DashboardCardSkeleton minHeight="min-h-[24rem]" lines={6} />
        <DashboardCardSkeleton minHeight="min-h-[24rem]" lines={6} />
      </div>
    );
  }

  const isMarketPending = isOwnerRequestPending(patrimony.marketIndicesStatus, enabled);
  const isPropertyPending = isOwnerRequestPending(patrimony.propertyReturnsStatus, enabled);
  const isFinancingPending = isOwnerRequestPending(patrimony.financingInsightsStatus, enabled);

  const shouldHideFinancing =
    patrimony.financingInsightsStatus === "error" &&
    patrimony.allFinancingInsights.length === 0 &&
    patrimony.financingInsights.length === 0;

  return (
    <div
      className={`grid w-full min-w-0 gap-4 ${
        shouldHideFinancing ? "grid-cols-1" : "grid-cols-1 xl:grid-cols-2"
      }`}
    >
      <div className="min-w-0">
        <PropertyReturns
          marketIndices={patrimony.marketIndices}
          propertyReturns={patrimony.propertyReturns}
          propertyPagination={patrimony.propertyPagination}
          registerPropertyReturns={patrimony.allPropertyReturns}
          payments={payments}
          propertyFilterOptions={propertyFilterOptions}
          yieldBasis={patrimony.yieldBasis}
          smDown={smDown}
          lgDown={lgDown}
          isMarketLoading={isMarketPending}
          isMarketError={patrimony.marketIndicesStatus === "error"}
          isPropertyLoading={isPropertyPending}
          isPropertyError={patrimony.propertyReturnsStatus === "error"}
          onPageChange={patrimony.loadPropertyReturnsPage}
          onYieldBasisChange={patrimony.setYieldBasis}
          onRefresh={patrimony.refreshPropertyReturns}
          onRetryMarketIndices={patrimony.retryMarketIndices}
          onRetryPropertyReturns={patrimony.retryPropertyReturns}
        />
      </div>

      {!shouldHideFinancing && (
        <div className="min-w-0">
          <FinancingInsights
            financingInsights={patrimony.financingInsights}
            financingPagination={patrimony.financingPagination}
            allFinancingInsights={patrimony.allFinancingInsights}
            propertyReturns={patrimony.allPropertyReturns}
            payments={payments}
            propertyFilterOptions={propertyFilterOptions}
            marketIndices={patrimony.marketIndices}
            smDown={smDown}
            lgDown={lgDown}
            isLoading={isFinancingPending}
            onPageChange={patrimony.loadFinancingInsightsPage}
            onRefresh={patrimony.refreshFinancingInsights}
          />
        </div>
      )}
    </div>
  );
}
